import { geoEquirectangular, geoPath } from 'd3-geo';
import { select } from 'd3-selection';
import { zoom as d3Zoom, zoomIdentity } from 'd3-zoom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowClockwise } from 'shared/icons';
import { feature } from 'topojson-client';
import land110m from 'world-atlas/land-110m.json';
import { MAP_EXTENT, REFERENCE_PORTS } from '../../constants/navios';
import { FormatBrazilDate } from '../../utils/dateUtils';

// ─────────────────────────────────────────────────────────────────────────────
// Mapa Ásia → Santos dos navios acompanhados. SVG puro: d3-geo projeta, o
// contorno da terra é o world-atlas land-110m EMBUTIDO no bundle (sem tiles
// externos, sem chave de API) e o d3-zoom dá arrastar/zoom (pinça no celular,
// roda no desktop). Marcadores e trilhas são reprojetados a cada zoom para
// manter o tamanho na tela; só o contorno da terra escala por transform.
// ─────────────────────────────────────────────────────────────────────────────

const LAND = feature(land110m, land110m.objects.land);
const MAX_ZOOM = 12;
const MARKER_RADIUS = 6;
const HIT_RADIUS = 16;
// Rótulo de porto some quando um navio está em cima dele (o nome do navio vence).
const PORT_LABEL_CLEARANCE = 48;
// Altura aproximada do tooltip (até 4 linhas) para mantê-lo dentro do mapa.
const TOOLTIP_HEIGHT = 96;
const EXTENT_ASPECT = (MAP_EXTENT.east - MAP_EXTENT.west) / (MAP_EXTENT.north - MAP_EXTENT.south);

/** Largura aproximada do texto (11px, semibold) para decidir de que lado o rótulo cabe. */
const approxTextWidth = (text, pxPerChar = 6.6) => String(text || '').length * pxPerChar;

const EXTENT_FEATURE = {
    type: 'Feature',
    geometry: {
        type: 'MultiPoint',
        coordinates: [
            [MAP_EXTENT.west, MAP_EXTENT.south],
            [MAP_EXTENT.east, MAP_EXTENT.north],
        ],
    },
};

const formatReading = (iso) =>
    iso ? FormatBrazilDate(iso, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

function useElementWidth(ref) {
    const [width, setWidth] = useState(0);
    useEffect(() => {
        const element = ref.current;
        if (!element) {
            return undefined;
        }
        const observer = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref]);
    return width;
}

const NaviosMap = ({ vessels, colorOf, selectedId = null, onSelect }) => {
    const containerRef = useRef(null);
    const svgRef = useRef(null);
    const zoomRef = useRef(null);
    const width = useElementWidth(containerRef);
    // Celular: altura na proporção do enquadramento (Ásia → Santos é largo e
    // baixo) para não sobrar faixa vazia; desktop: altura fixa.
    const height = width < 640 ? Math.max(200, Math.round(width / EXTENT_ASPECT) + 32) : 380;
    const [transform, setTransform] = useState(zoomIdentity);
    const [hoverId, setHoverId] = useState(null);

    const projection = useMemo(
        () =>
            width > 0
                ? geoEquirectangular().fitExtent(
                      [
                          [8, 8],
                          [width - 8, height - 8],
                      ],
                      EXTENT_FEATURE,
                  )
                : null,
        [width, height],
    );
    const landPath = useMemo(() => (projection ? geoPath(projection)(LAND) : null), [projection]);

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg || width === 0) {
            return undefined;
        }
        const behavior = d3Zoom()
            .scaleExtent([1, MAX_ZOOM])
            .translateExtent([
                [0, 0],
                [width, height],
            ])
            .extent([
                [0, 0],
                [width, height],
            ])
            .on('zoom', (event) => setTransform(event.transform));
        zoomRef.current = behavior;
        const selection = select(svg);
        selection.call(behavior);
        return () => selection.on('.zoom', null);
    }, [width, height]);

    const zoomBy = useCallback((factor) => {
        if (svgRef.current && zoomRef.current) {
            zoomRef.current.scaleBy(select(svgRef.current), factor);
        }
    }, []);
    const resetZoom = useCallback(() => {
        if (svgRef.current && zoomRef.current) {
            zoomRef.current.transform(select(svgRef.current), zoomIdentity);
        }
    }, []);

    // Projeção + zoom → coordenadas de tela (o marcador não cresce com o zoom).
    const toScreen = useCallback(
        (lat, lon) => {
            const point = projection?.([lon, lat]);
            return point ? transform.apply(point) : null;
        },
        [projection, transform],
    );

    const drawn = useMemo(
        () =>
            vessels
                .filter((vessel) => vessel.last_position)
                .map((vessel) => {
                    const trail = (vessel.trail || []).map((point) => toScreen(point.lat, point.lon)).filter(Boolean);
                    // Trechos já calculados no backend: leituras próximas em linha
                    // reta; buraco sem leitura segue a rota marítima (tracejado),
                    // para a trilha não cortar continente.
                    const segments = (
                        vessel.trail_segments || [
                            { estimated: false, points: (vessel.trail || []).map((p) => [p.lat, p.lon]) },
                        ]
                    )
                        .map((segment) => ({
                            estimated: segment.estimated,
                            points: segment.points.map(([lat, lon]) => toScreen(lat, lon)).filter(Boolean),
                        }))
                        .filter((segment) => segment.points.length > 1);
                    return {
                        vessel,
                        color: colorOf(vessel.id),
                        head: toScreen(vessel.last_position.lat, vessel.last_position.lon),
                        trail,
                        segments,
                    };
                })
                .filter((entry) => entry.head),
        [vessels, colorOf, toScreen],
    );

    const tooltipEntry = drawn.find((entry) => entry.vessel.id === (hoverId || selectedId)) || null;

    return (
        <div
            ref={containerRef}
            className="relative overflow-hidden rounded-xl border border-mono-200 bg-mono-50 dark:border-mono-800 dark:bg-mono-950"
            style={{ height }}
        >
            {width > 0 && projection ? (
                <svg
                    ref={svgRef}
                    width={width}
                    height={height}
                    className="block cursor-grab touch-none select-none active:cursor-grabbing"
                    role="img"
                    aria-label="Mapa dos navios entre a Ásia e Santos"
                    onClick={(event) => {
                        if (event.target === svgRef.current) {
                            setHoverId(null);
                        }
                    }}
                >
                    <g transform={transform.toString()}>
                        <path d={landPath} className="fill-mono-200 dark:fill-mono-800" />
                    </g>

                    {REFERENCE_PORTS.map((port) => {
                        const point = toScreen(port.lat, port.lon);
                        if (!point) {
                            return null;
                        }
                        const covered = drawn.some(
                            ({ head }) => Math.hypot(head[0] - point[0], head[1] - point[1]) < PORT_LABEL_CLEARANCE,
                        );
                        const labelLeft = point[0] > width - 70;
                        return (
                            <g key={port.key} transform={`translate(${point[0]},${point[1]})`} pointerEvents="none">
                                <rect
                                    x={-3}
                                    y={-3}
                                    width={6}
                                    height={6}
                                    transform="rotate(45)"
                                    className="fill-mono-500 stroke-mono-50 dark:fill-mono-400 dark:stroke-mono-950"
                                    strokeWidth={1.5}
                                />
                                {covered ? null : (
                                    <text
                                        x={labelLeft ? -7 : 7}
                                        y={port.key === 'santos' ? 12 : -6}
                                        textAnchor={labelLeft ? 'end' : 'start'}
                                        className="fill-mono-500 stroke-mono-50 text-[10px] dark:fill-mono-400 dark:stroke-mono-950"
                                        strokeWidth={3}
                                        style={{ paintOrder: 'stroke' }}
                                    >
                                        {port.label}
                                    </text>
                                )}
                            </g>
                        );
                    })}

                    {drawn.map(({ vessel, color, trail, segments }) =>
                        trail.length > 1 ? (
                            <g key={`trail-${vessel.id}`} pointerEvents="none">
                                {segments.map((segment, index) => (
                                    <polyline
                                        key={index}
                                        points={segment.points.map((point) => point.join(',')).join(' ')}
                                        fill="none"
                                        stroke={color}
                                        strokeWidth={selectedId === vessel.id ? 3 : 2}
                                        strokeOpacity={selectedId && selectedId !== vessel.id ? 0.35 : 0.8}
                                        strokeDasharray={segment.estimated ? '5 4' : undefined}
                                        strokeLinejoin="round"
                                        strokeLinecap="round"
                                    />
                                ))}
                                {trail.slice(0, -1).map((point, index) => (
                                    <circle
                                        key={index}
                                        cx={point[0]}
                                        cy={point[1]}
                                        r={2}
                                        fill={color}
                                        fillOpacity={0.6}
                                    />
                                ))}
                            </g>
                        ) : null,
                    )}

                    {drawn.map(({ vessel, color, head }) => {
                        const isSelected = selectedId === vessel.id;
                        const dimmed = selectedId && !isSelected;
                        // Perto da borda direita o nome vai para a esquerda do marcador.
                        const labelLeft = head[0] + MARKER_RADIUS + 5 + approxTextWidth(vessel.name) > width - 4;
                        return (
                            // biome-ignore lint/a11y/useSemanticElements: marcador SVG não pode ser <button>; foco e Enter tratados aqui
                            <g
                                key={vessel.id}
                                transform={`translate(${head[0]},${head[1]})`}
                                role="button"
                                tabIndex={0}
                                aria-label={`${vessel.name}: ${vessel.onde_esta?.frase || ''}`}
                                className="cursor-pointer outline-none"
                                opacity={dimmed ? 0.55 : 1}
                                onMouseEnter={() => setHoverId(vessel.id)}
                                onMouseLeave={() => setHoverId(null)}
                                onFocus={() => setHoverId(vessel.id)}
                                onBlur={() => setHoverId(null)}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onSelect?.(vessel.id);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        onSelect?.(vessel.id);
                                    }
                                }}
                            >
                                <circle r={HIT_RADIUS} fill="transparent" />
                                <circle
                                    r={isSelected ? MARKER_RADIUS + 2 : MARKER_RADIUS}
                                    fill={color}
                                    className="stroke-mono-50 dark:stroke-mono-950"
                                    strokeWidth={2}
                                />
                                <text
                                    x={labelLeft ? -(MARKER_RADIUS + 5) : MARKER_RADIUS + 5}
                                    y={4}
                                    textAnchor={labelLeft ? 'end' : 'start'}
                                    className="fill-black stroke-mono-50 text-[11px] font-semibold dark:fill-white dark:stroke-mono-950"
                                    strokeWidth={3}
                                    style={{ paintOrder: 'stroke' }}
                                >
                                    {vessel.name}
                                </text>
                            </g>
                        );
                    })}
                </svg>
            ) : null}

            {tooltipEntry ? (
                <div
                    className="pointer-events-none absolute z-10 max-w-[260px] rounded-lg border border-mono-200 bg-white px-3 py-2 text-xs shadow-md dark:border-mono-700 dark:bg-mono-900"
                    style={{
                        left: Math.min(Math.max(tooltipEntry.head[0] + 12, 8), Math.max(8, width - 268)),
                        // Acima ou abaixo do marcador, sempre dentro do mapa (que é baixo no celular).
                        top: Math.min(
                            Math.max(
                                tooltipEntry.head[1] > height / 2
                                    ? tooltipEntry.head[1] - TOOLTIP_HEIGHT - 10
                                    : tooltipEntry.head[1] + 14,
                                4,
                            ),
                            height - TOOLTIP_HEIGHT - 4,
                        ),
                    }}
                >
                    <div className="flex items-center gap-1.5 font-semibold text-black dark:text-white">
                        <span
                            className="inline-block h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: tooltipEntry.color }}
                        />
                        {tooltipEntry.vessel.name}
                    </div>
                    <div className="mt-0.5 text-mono-700 dark:text-mono-300">
                        {tooltipEntry.vessel.onde_esta?.frase || '—'}
                    </div>
                    <div className="mt-0.5 text-mono-500 dark:text-mono-400">
                        Leitura {formatReading(tooltipEntry.vessel.last_position.observed_at)}
                    </div>
                </div>
            ) : null}

            {drawn.some((entry) => entry.segments.some((segment) => segment.estimated)) ? (
                <div className="pointer-events-none absolute bottom-2 left-2 flex items-center gap-1.5 rounded bg-white/80 px-1.5 py-0.5 text-[10px] text-mono-600 dark:bg-mono-900/80 dark:text-mono-400">
                    <svg width="18" height="4" aria-hidden="true">
                        <line
                            x1="0"
                            y1="2"
                            x2="18"
                            y2="2"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeDasharray="5 4"
                        />
                    </svg>
                    sem leitura: rota estimada
                </div>
            ) : null}

            <div className="absolute bottom-2 right-2 flex flex-col overflow-hidden rounded-lg border border-mono-200 bg-white text-black shadow-sm dark:border-mono-700 dark:bg-mono-900 dark:text-white">
                <button
                    type="button"
                    className="h-8 w-8 text-lg leading-none hover:bg-mono-100 dark:hover:bg-mono-800"
                    onClick={() => zoomBy(1.6)}
                    aria-label="Aproximar"
                >
                    +
                </button>
                <button
                    type="button"
                    className="h-8 w-8 border-t border-mono-200 text-lg leading-none hover:bg-mono-100 dark:border-mono-700 dark:hover:bg-mono-800"
                    onClick={() => zoomBy(1 / 1.6)}
                    aria-label="Afastar"
                >
                    −
                </button>
                <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center border-t border-mono-200 hover:bg-mono-100 dark:border-mono-700 dark:hover:bg-mono-800"
                    onClick={resetZoom}
                    aria-label="Enquadrar Ásia → Santos"
                    title="Enquadrar Ásia → Santos"
                >
                    <ArrowClockwise size={14} />
                </button>
            </div>
        </div>
    );
};

export default NaviosMap;
